import {
  ActionIcon,
  AppShell,
  Badge,
  Box,
  Burger,
  Button,
  Card,
  Flex,
  Group,
  Image,
  Overlay,
  ScrollArea,
  Text,
} from '@mantine/core'
import { IconCalendarStats, IconUser, IconX } from '@tabler/icons-react'
import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { setPageAccessOverrides } from '@/auth/authorizations'
import { useAuth } from '@/auth/use-auth'
import ScrollToTop from '@/components/ScrollToTop'
import { usePageAccess, writeStoredPageAccess } from '@/hooks/constant/usePageAccess'
import { useAutoBookingsQueueSize } from '@/hooks/customer/useAutoBookingsQueueSize'
import { useNewLeadsQueueSize } from '@/hooks/customer/useNewLeadsQueueSize'
import { useSalesReviewCount } from '@/hooks/customer/useSalesReviewCount'
import { usePendingApprovalsCount } from '@/hooks/performanceLog/usePendingApprovalsCount'
import { RoleEnum } from '@/models'
import { EmmaPath } from '@/router/paths'
import { getPrefetchHandlers } from '@/router/prefetch'

import AddCustomerButton from '../AddCustomerButton/AddCustomerButton'
import LogoutButton from '../LogoutButton'
import { menuItems } from '../utils'
import { useColorSchemeStore } from './stores/useColorSchemeStore'
import { useSidebarStore } from './stores/useSidebarStore'

export function MainLayout() {
  const { openedSidebar, toggleSidebar, openedMobile, toggleMobile, setMobileOpen } = useSidebarStore()
  const { getColors } = useColorSchemeStore()
  const colors = getColors()

  const navigate = useNavigate()
  const location = useLocation()

  const { userRoles, userName } = useAuth()

  const handleMobileNavigation = (path: string, external?: boolean) => {
    if (external) {
      window.open(path, '_blank', 'noopener,noreferrer')
    } else {
      navigate(path)
    }
    setMobileOpen(false)
  }

  const hasOfficeRole = userRoles.some((role) =>
    [RoleEnum.admin, RoleEnum.manager, RoleEnum.sales, RoleEnum.super_admin].includes(role as RoleEnum),
  )

  const { data: newLeadsQueueSize, isLoading: isLoadingNewLeads } = useNewLeadsQueueSize(hasOfficeRole)

  const { data: autoBookingsQueueSize, isLoading: isLoadingAutoBookings } = useAutoBookingsQueueSize(hasOfficeRole)

  const { data: pendingApprovalsCount } = usePendingApprovalsCount()

  const { data: salesReviewCount, isLoading: isLoadingSalesReview } = useSalesReviewCount(hasOfficeRole)

  const { overrides: pageAccessOverrides, isSettled: pageAccessSettled } = usePageAccess()

  // Make the overrides visible to canView/userCanPerformAction everywhere
  // (sidebar, PageGuard, buttons) and cache them for the next load.
  setPageAccessOverrides(pageAccessOverrides)
  useEffect(() => {
    if (pageAccessSettled) writeStoredPageAccess(pageAccessOverrides)
  }, [pageAccessOverrides, pageAccessSettled])

  return (
    <>
      <AppShell
        layout="alt"
        header={{ height: { base: 60, sm: 0 } }}
        navbar={{ width: openedSidebar ? 200 : 75, breakpoint: 'sm', collapsed: { mobile: !openedMobile } }}
        padding="md"
      >
        <AppShell.Header p="md" bg={colors.background} hiddenFrom="sm">
          <Group justify="space-between" align="center" h="100%">
            <Image src="/logo.png" h={40} w="auto" fit="contain" alt="logo" />
            <Burger
              opened={openedMobile}
              size="sm"
              color="white"
              aria-label={openedMobile ? 'Close navigation menu' : 'Open navigation menu'}
              onClick={toggleMobile}
            />
          </Group>
        </AppShell.Header>
        <AppShell.Navbar p="md" bg={colors.background} bd={0} role="navigation" aria-label="Main navigation">
          <AppShell.Section>
            <Group justify={openedSidebar ? 'flex-end' : 'center'}>
              <Burger
                opened={openedSidebar}
                size="sm"
                color="white"
                visibleFrom="sm"
                aria-label={openedSidebar ? 'Collapse sidebar' : 'Expand sidebar'}
                onClick={toggleSidebar}
              />
              <ActionIcon
                variant="subtle"
                color="white"
                size="lg"
                hiddenFrom="sm"
                aria-label="Close navigation menu"
                onClick={() => setMobileOpen(false)}
              >
                <IconX size={20} />
              </ActionIcon>
            </Group>
            <Flex direction="column" align="center" p="0" m="0">
              <Image src="/logo.png" radius="md" h={86.26} w="auto" fit="contain" alt="logo" my="md" visibleFrom="sm" />
              <AddCustomerButton openedSidebar={openedSidebar} />
            </Flex>
          </AppShell.Section>
          <AppShell.Section grow my={openedSidebar ? 'md' : 'xl'} component={ScrollArea}>
            <Flex direction="column" gap={openedSidebar ? 'md' : 'xl'}>
              {menuItems(
                userRoles,
                isLoadingNewLeads ? undefined : (newLeadsQueueSize?.size ?? 0),
                isLoadingAutoBookings ? undefined : (autoBookingsQueueSize?.size ?? 0),
                isLoadingSalesReview ? undefined : (salesReviewCount?.size ?? 0),
                pendingApprovalsCount ?? undefined,
              )
                .filter((item) => !item.disabled)
                .map((item) => {
                  if (item.subMenu.items.some((subItem) => subItem.authorized && !subItem.disabled)) {
                    return (
                      <Flex
                        key={`${item.label}-${item.subMenu.items.length}`}
                        direction="column"
                        justify={openedSidebar ? 'flex-start' : 'center'}
                        align={openedSidebar ? 'flex-start' : 'center'}
                        gap={1}
                      >
                        {openedSidebar ? (
                          <Text fw={400} size="xs" c={colors.textSecondary}>
                            {item.label}
                          </Text>
                        ) : null}
                        {item.subMenu.items.map((subItem) => {
                          if (subItem.disabled) {
                            return null
                          }
                          if (subItem.authorized) {
                            const isActive =
                              !subItem.external &&
                              (location.pathname === subItem.path || location.pathname.includes(subItem.path))
                            return openedSidebar ? (
                              <Button
                                key={subItem.label}
                                autoContrast
                                variant="subtle"
                                leftSection={
                                  <subItem.icon size={18} color={isActive ? colors.textPrimary : colors.accent} />
                                }
                                fw={isActive ? 700 : 400}
                                w="100%"
                                size="xs"
                                justify="flex-start"
                                ff="Roboto"
                                bg={isActive ? colors.backgroundHover : ''}
                                c={colors.textPrimary}
                                onClick={() => handleMobileNavigation(subItem.path, subItem.external)}
                                {...(subItem.external ? {} : getPrefetchHandlers(subItem.path))}
                              >
                                {subItem.label}
                                {subItem.badge !== undefined && (
                                  <Badge
                                    color={colors.alertBadge}
                                    size="xs"
                                    radius="xl"
                                    variant="filled"
                                    styles={{
                                      root: {
                                        position: 'absolute',
                                        right: 4,
                                        minWidth: 22,
                                        height: 22,
                                        pointerEvents: 'none',
                                      },
                                    }}
                                  >
                                    {subItem.badge}
                                  </Badge>
                                )}
                              </Button>
                            ) : (
                              <Box
                                key={`${subItem.label}-${item.label}`}
                                style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                                onClick={() => handleMobileNavigation(subItem.path, subItem.external)}
                                {...(subItem.external ? {} : getPrefetchHandlers(subItem.path))}
                              >
                                <ActionIcon
                                  variant="subtle"
                                  aria-label={subItem.label}
                                  color={isActive ? colors.textPrimary : colors.accent}
                                >
                                  <subItem.icon size={18} color={isActive ? colors.textPrimary : colors.accent} />
                                </ActionIcon>
                                {subItem.badge !== undefined && (
                                  <Badge
                                    color={colors.alertBadge}
                                    radius="xl"
                                    variant="filled"
                                    styles={{
                                      root: {
                                        position: 'absolute',
                                        bottom: -2,
                                        right: -4,
                                        minWidth: 16,
                                        height: 16,
                                        padding: '0 2px',
                                        fontSize: '8px',
                                        pointerEvents: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      },
                                    }}
                                  >
                                    {subItem.badge}
                                  </Badge>
                                )}
                              </Box>
                            )
                          }
                          return null
                        })}
                      </Flex>
                    )
                  }
                  return null
                })}
            </Flex>
          </AppShell.Section>
          <AppShell.Section>
            <Card bg={'#FFFFFF0D'} padding={1}>
              <Flex
                justify="center"
                align={openedSidebar ? 'flex-start' : 'center'}
                direction="column"
                gap={openedSidebar ? '0' : 'xs'}
              >
                {openedSidebar ? (
                  <Button
                    variant="subtle"
                    leftSection={<IconUser size={18} fontWeight={700} />}
                    color="#FFFFFF"
                    fw={700}
                    w="100%"
                    size="xs"
                    justify="flex-start"
                    onClick={() =>
                      userRoles.includes(RoleEnum.admin) ||
                      userRoles.includes(RoleEnum.mover) ||
                      userRoles.includes(RoleEnum.team_lead)
                        ? handleMobileNavigation(EmmaPath.ACCOUNT_MY_SCHEDULE)
                        : null
                    }
                  >
                    {userName}
                  </Button>
                ) : (
                  <ActionIcon
                    variant="subtle"
                    aria-label="User"
                    color="#FFFFFF66"
                    onClick={() =>
                      userRoles.includes(RoleEnum.admin) ||
                      userRoles.includes(RoleEnum.mover) ||
                      userRoles.includes(RoleEnum.team_lead)
                        ? handleMobileNavigation(EmmaPath.ACCOUNT_MY_SCHEDULE)
                        : null
                    }
                  >
                    <IconUser size={18} />
                  </ActionIcon>
                )}
                {userRoles.includes(RoleEnum.admin) ||
                userRoles.includes(RoleEnum.mover) ||
                userRoles.includes(RoleEnum.team_lead) ? (
                  openedSidebar ? (
                    <Button
                      variant="subtle"
                      leftSection={<IconCalendarStats size={18} color="#FFFFFF66" />}
                      color="#FFFFFF"
                      fw={400}
                      w="100%"
                      size="xs"
                      justify="flex-start"
                      onClick={() => handleMobileNavigation(EmmaPath.ACCOUNT_MY_SCHEDULE)}
                      {...getPrefetchHandlers(EmmaPath.ACCOUNT_MY_SCHEDULE)}
                    >
                      My Schedule
                    </Button>
                  ) : (
                    <ActionIcon
                      variant="subtle"
                      aria-label="My Schedule"
                      color="#FFFFFF66"
                      onClick={() => handleMobileNavigation(EmmaPath.ACCOUNT_MY_SCHEDULE)}
                    >
                      <IconCalendarStats size={18} />
                    </ActionIcon>
                  )
                ) : null}
                <LogoutButton openedSidebar={openedSidebar} />
              </Flex>
            </Card>
          </AppShell.Section>
        </AppShell.Navbar>
        <AppShell.Main bg="#f6f6f6" id="main-content" role="main" aria-label="Main content">
          <ScrollToTop />
          {openedMobile && (
            <Overlay
              color="#000"
              backgroundOpacity={0.3}
              blur={2}
              pos="fixed"
              top={0}
              left={0}
              right={0}
              bottom={0}
              style={{ zIndex: 100 }}
              hiddenFrom="sm"
              onClick={() => setMobileOpen(false)}
            />
          )}
          <Outlet />
        </AppShell.Main>
      </AppShell>
    </>
  )
}
